/**
 * DomainProvider — subscribes to schools/{schoolId}/domains and writes to domainStore.
 *
 * Mount this above any component that renders DomainTags or reads from useDomainStore.
 * It is safe to mount multiple times — the store deduplicates domain state.
 *
 * Usage:
 *   <DomainProvider schoolId={schoolId}>
 *     <YourComponent />
 *   </DomainProvider>
 */

import { createContext, useContext, useEffect } from 'react';
import { subscribeDomains } from '../../../services/domainRegistry';
import { useDomainStore } from '../../../stores/domainStore';
import { useDomainRegistry } from '../../../services/domain/useDomainRegistry';

const RegistryContext = createContext(null);

/**
 * @param {Object} props
 * @param {string} props.schoolId
 * @param {React.ReactNode} props.children
 */
export function DomainProvider({ schoolId, children }) {
  const parent = useContext(RegistryContext);
  if (parent && parent.schoolId === schoolId) return children;
  return <RegistrySubscription schoolId={schoolId}>{children}</RegistrySubscription>;
}

function RegistrySubscription({ schoolId, children }) {
  const registry = useDomainRegistry(schoolId, subscribeDomains);
  const setRegistryState = useDomainStore(state => state.setRegistryState);
  useEffect(() => { setRegistryState(registry); }, [registry, setRegistryState]);
  return <RegistryContext.Provider value={registry}>{children}</RegistryContext.Provider>;
}

export default DomainProvider;
